import {
  AfterViewChecked,
  ApplicationRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { OrderformFascade } from '../../fascades/orderform.fascade';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { CustomFormData, FormFields, InterfacePost } from '../../../index';
import { BehaviorSubject, combineLatest, merge, Subject } from 'rxjs';
import { InputsGeneratorComponent } from '../inputs-generator/inputs-generator.component';
import {
  AbstractControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

export const INPUT_ADDRESS_INPUT_DEBOUNCE = 750;

export function compareWithFieldValidator(
  compareFieldName: string,
  formGroup?: UntypedFormGroup
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = (control.parent || formGroup) as UntypedFormGroup;
    const compareValue = parent.get(compareFieldName).value;
    return control.value !== compareValue
      ? {
          custom: $localize`:@@form.input.repeat_error_email:Die E-Mail-Adressen stimmen nicht überein.`,
        }
      : null;
  };
}

@Component({
  selector: 'orderform-input-address',
  templateUrl: './input-address.component.html',
  styleUrls: ['./input-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputAddressComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private _repeatEmail$ = new BehaviorSubject<boolean>(false);

  @Input() set repeatEmail(repeatEmailAddress: string) {
    this._repeatEmail$.next(repeatEmailAddress.toLowerCase() === 'true');
  }

  @ViewChild('deliveryAddressEl', { static: true })
  deliveryAddressEl: ElementRef;
  @ViewChild('billingAddressEl', { static: true }) billingAddressEl: ElementRef;

  @ViewChild('deliveryAddressInput', { static: false })
  deliveryAddressInput: InputsGeneratorComponent;

  @ViewChild('billingAddressInput', { static: false })
  billingAddressInput: InputsGeneratorComponent;

  /**
   * get order data
   */
  order$ = this._orderFormFascade.order$;

  triggerCDR$ = new Subject<void>();

  /**
   * get contact form settings
   */
  settingsContactForm$ = combineLatest([
    this._orderFormFascade.settings$.pipe(map((e) => e.contact_form)),
    this._repeatEmail$,
  ]).pipe(
    map(([contactFormFields, repeatEmail]) => {
      const modificationShippingFields: FormFields.Field[] = [];
      contactFormFields.fields_shipping_address.forEach((field) => {
        modificationShippingFields.push(field);

        /**
         * Add fake field if emails should repeat
         */
        if (field.postname === 'buyer_address_email' && repeatEmail) {
          const repeatField = this.createEmailRepeatOfField(field);
          modificationShippingFields.push(repeatField);
        }
      });
      return {
        ...contactFormFields,
        fields_shipping_address: modificationShippingFields,
      };
    }),
    shareReplay(1),
    tap(() => {
      /**
       * Dirty but it works https://digistore.atlassian.net/browse/DS-10738
       * if the google street suggest was selected, the country can be trigger an update. Ok!
       * But this update of the store doesn't trigger "mark for check" all parent components (generator-input > inputs-generator > input-address)
       * because the google layer is outside of the angular zone...
       * So, we need to trigger the cdr manually. if the fields changes.
       */
      this.triggerCDR$.next();
    })
  );

  createEmailRepeatOfField(field: FormFields.Field): FormFields.Field {
    const repeatField: FormFields.Field = {
      ...field,
      postname: field.postname + '_repeat',
      id: field.id + '_repeat',
      label:
        $localize`:@@form.input.repeat_field:[INPUT_FIELD_NAME] wiederholen`.replace(
          '[INPUT_FIELD_NAME]',
          field.label
        ),
      placeholder:
        $localize`:@@form.input.repeat_field:[INPUT_FIELD_NAME] wiederholen`.replace(
          '[INPUT_FIELD_NAME]',
          field.placeholder
        ),
      validators: [compareWithFieldValidator(field.postname)],
    };
    return repeatField;
  }

  filledBillingAddress$ = this.order$.pipe(
    map((order) => order.order.billing_address)
  );
  filledShippingAddress$ = this.order$.pipe(
    map((order) => order.order.shipping_address)
  );

  private _destroy$ = new Subject<void>();
  private _onAfterContentChecked$ = new Subject<void>();

  deliveryAddressData$ = new BehaviorSubject<CustomFormData>({});
  billingAddressData$ = new BehaviorSubject<CustomFormData>({});
  manualBillingAddressIsSame$ = new BehaviorSubject<boolean | null>(null);

  @Output() readonly validate = new EventEmitter<{
    valid: boolean;
    source: 'submit' | 'change';
  }>();

  private _submitFailedSubscription =
    this._orderFormFascade.submitFailed.subscribe(() => {
      const valid = this._isValid();
      this.validate.emit({ valid, source: 'submit' });
    });

  private _formValidationChangeSubscription = this._onAfterContentChecked$
    .pipe(
      switchMap(() =>
        this.billingAddressInput
          ? merge(
              this.billingAddressInput.stateChange,
              this.deliveryAddressInput.stateChange
            )
          : this.deliveryAddressInput.stateChange
      ),
      map(() => {
        return this._isValid();
      }),
      distinctUntilChanged()
    )
    .subscribe((valid) => {
      this.validate.emit({ valid, source: 'change' });
    });

  isBillingAddressSameAsDeliveryOpen$ = combineLatest([
    this.manualBillingAddressIsSame$.pipe(
      distinctUntilChanged(),
      // Handle scroll to elements
      tap((billingAddressIsSame: boolean | null) => {
        if (billingAddressIsSame !== null) {
          // if manual open true, scroll into billing address
          if (billingAddressIsSame) {
            if (this.deliveryAddressEl) {
              this.deliveryAddressEl.nativeElement.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
              });
            }
          }
          // if manualOpen false, scroll into delivery address
          else {
            if (this.billingAddressEl) {
              this.billingAddressEl.nativeElement.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
              });
            }
          }
        }
      })
    ),
    this.settingsContactForm$.pipe(
      map((contactSettings) => !contactSettings.is_billing_address_form_open),
      distinctUntilChanged()
    ),
  ]).pipe(
    map(([manualOpen, isBillingAddressSame]) => {
      // if manual open billing address
      if (manualOpen !== null) {
        return manualOpen;
      } else {
        return isBillingAddressSame;
      }
    }),
    shareReplay(1)
  );

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _cdr: ChangeDetectorRef,
    private _appRef: ApplicationRef
  ) {}

  ngOnInit(): void {
    // prevent to many cdr with debounce
    this.triggerCDR$
      .pipe(takeUntil(this._destroy$), debounceTime(100))
      .subscribe(() => {
        this._cdr.detectChanges();
      });

    combineLatest([
      this.deliveryAddressData$,
      this.billingAddressData$,
      this.isBillingAddressSameAsDeliveryOpen$,
    ])
      .pipe(
        takeUntil(this._destroy$),
        map(([deliveryAddress, billingAddress, isBillingAddressDifferent]) => {
          // merge data and set marker for has billigAddress data
          const updateData: InterfacePost = {
            has_billing_address: Number(!isBillingAddressDifferent),
          };

          if (Object.keys(deliveryAddress).length > 0) {
            updateData.shipping_address = deliveryAddress;
          }

          if (Object.keys(billingAddress).length > 0) {
            updateData.billing_address = billingAddress;
          }

          this._orderFormFascade.setOrderStore(updateData);
        })
      )
      .subscribe();

    this.deliveryAddressData$
      .pipe(
        takeUntil(this._destroy$),
        map((data) => data.buyer_address_email),
        distinctUntilChanged(),
        filter(() =>
          this.deliveryAddressInput?.isFieldValid('buyer_address_email')
        ),
        debounceTime(INPUT_ADDRESS_INPUT_DEBOUNCE)
      )
      .subscribe((email) => {
        this._orderFormFascade.checkDoubleOrder(email);
      });
  }

  ngAfterViewChecked() {
    this._onAfterContentChecked$.next();
  }

  private _isValid() {
    return this.billingAddressInput
      ? this.billingAddressInput.isValid() &&
          this.deliveryAddressInput.isValid()
      : this.deliveryAddressInput.isValid();
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._submitFailedSubscription.unsubscribe();
    this._formValidationChangeSubscription.unsubscribe();
  }
}
